import { TFunction } from 'i18next';
import { NeedPath, needPathShare } from './recipeExplosion';
import { fmtCountUnit } from './shared';

/**
 * Turn a dish's recipe routes into the drawer's prep breakdown lines — the sentence that
 * makes a per-dish figure checkable when the product arrives through a prep.
 *
 * Both breakdown drawers (Product Utilization, Inventory Leakage) render the same
 * `per dish × sold = needed` card, and both flatten the prep chain into that single
 * "per dish" number. That number is correct but unexplained: a chef cannot see how a prep
 * became 140 g on the card. These lines state the missing step as the SHARE of the prep
 * the dish consumes —
 *
 *   `via Beef ragu — 1 p of 10 p (10% of batch) → 140 g`
 *
 * Deliberately NOT the prep's own row quantity (the "4 000 g" of the original bug report):
 * that raw batch number is what made the card unreadable, and the share conveys the same
 * step without it. See `NeedPath` in `recipeExplosion.ts`.
 *
 * Hop quantities print in the PREP's own `portionsUnit` via `fmtCountUnit` (free text the
 * customer typed, same as a count unit — never rescaled like a base unit). The per-dish
 * amount goes through the caller's `fmtProductQty`, because the two pages format product
 * quantities differently (utilization: `fmtBase`; leakage: the row's count-unit-aware
 * formatter) and a breakdown that disagreed with the card above it would be worse than none.
 *
 * Returns [] for an empty/all-direct route set, so a caller can render unconditionally and
 * a dish with no preps shows the card exactly as it did before this existed.
 */
export function prepPathLines(
  paths: NeedPath[] | undefined,
  fmtProductQty: (value: number) => string,
  t: TFunction
): string[] {
  if (!paths || paths.length === 0) return [];
  return paths.map((path) => {
    const perDish = fmtProductQty(path.perPortionBase);
    // A direct row only ever appears ALONGSIDE a prep route (all-direct sets are dropped
    // upstream), where it explains the share of a blended per-dish number that never went
    // through a prep at all — without it those cards look like the preps under-account.
    if (path.hops.length === 0) return t('reports.utilization.drawer.prepPathDirect', { perDish });

    const share = formatShare(needPathShare(path));
    // One hop is the overwhelmingly common case and gets the plainest sentence. Deeper
    // chains name every prep and keep each hop's fraction, so "5% of batch" stays traceable
    // instead of appearing out of nowhere.
    if (path.hops.length === 1) {
      const hop = path.hops[0];
      return t('reports.utilization.drawer.prepPath', {
        name: hop.name,
        used: fmtCountUnit(hop.used, hop.unit),
        batchYield: fmtCountUnit(hop.batchYield, hop.unit),
        share,
        perDish,
      });
    }
    return t('reports.utilization.drawer.prepPathNested', {
      chain: path.hops
        .map((hop) =>
          t('reports.utilization.drawer.prepHop', {
            name: hop.name,
            used: fmtCountUnit(hop.used, hop.unit),
            batchYield: fmtCountUnit(hop.batchYield, hop.unit),
          })
        )
        .join(' → '),
      share,
      perDish,
    });
  });
}

/**
 * A batch share as a percentage. Precision follows magnitude so a small-but-real share
 * never prints as a flat "0%" — a garnish prep genuinely can be a hundredth of a batch,
 * and rounding that away would read as "this dish uses none of it".
 */
function formatShare(fraction: number): string {
  const pct = fraction * 100;
  if (pct > 0 && pct < 0.01) return '<0.01%';
  const decimals = pct >= 10 ? 0 : pct >= 1 ? 1 : 2;
  return `${Number(pct.toFixed(decimals))}%`;
}
